/**
 * Keeping the per-page maps in step with a page reorder.
 *
 * The annotation modal holds several records keyed by page number: the serialized annotations, the
 * undo stacks, and the id of the note auto-placement put on each page. A reorder renumbers all of
 * them at once, and a map left on the old numbering does not fail loudly — it silently describes a
 * different page than the one it is filed under.
 *
 * That is how a hardcopy note came to be burned into the image but not recorded: the note-id map
 * kept the old key, so the save looked for the note under a page number that by then held another
 * page's objects, found nothing, and recorded nothing.
 *
 * Pure, so the renumbering can be unit tested without fabric, pdf.js or pdf-lib present.
 */

/**
 * Renumber a page-keyed map onto a new page order.
 *
 * Falsy entries are dropped rather than carried across: for each of these maps an absent key and a
 * falsy value already mean the same thing — no annotations, no undo history, no auto-placed note —
 * and the readers treat them alike.
 *
 * @param byPage Map keyed by 1-based page number.
 * @param order The original 1-based page numbers in their new order, so `order[i]` names the page
 * that becomes page `i + 1`.
 */
export function remapPagesForReorder<T>(
    byPage: Record<number, T | undefined>,
    order: readonly number[]
): Record<number, T> {
    const remapped: Record<number, T> = {};

    order.forEach((originalPage, newIndex) => {
        const value = byPage[originalPage];
        if (value) remapped[newIndex + 1] = value;
    });

    return remapped;
}
